import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLeftPanel from '../Common/AuthLeftPanel';
import OtpInput from 'react-otp-input';
import { useResendOTPMutation, useVerifyOTPMutation } from 'services';
import useTranslate from 'hooks/useTranslate';
import BackBtn from 'components/Common/BackBtn';
import toast from 'react-hot-toast';
import { isMobile } from 'react-device-detect';

const VerifyOTP: React.FC = () => {
	const { translate } = useTranslate();
	const [error, setError] = useState<string | null>(null);
	const [message, setMessage] = useState(null);
	const [otp, setOtp] = useState('');
	const [searchParams] = useSearchParams();
	const [timer, setTimer] = useState(30);
	const [isTimerRunning, setIsTimerRunning] = useState(false);
	const navigate = useNavigate();

	// mutations
	const { mutateAsync: verifyOTPMutation } = useVerifyOTPMutation();
	const { mutateAsync: resendOTPMutation } = useResendOTPMutation();

	const userType = searchParams.get('userType');
	const userEmail = searchParams.get('email');

	useEffect(() => {
		if (!userEmail) {
			toast.error(translate('ToastMessage.invalidField', { field: 'Email' }));
			navigate('/forgot-password');
		}
	}, [userEmail]);
	const handleRequestOtp = async () => {
		// add resend requestOtpMutation code
		if (userEmail) {
			resendOTPMutation({ email: userEmail }).then((res: any) => {
				if (res?.success) {
					toast.success(res.message);
				}
			});
			setTimer(30);
			setIsTimerRunning(true);
		} else {
			toast.error(translate('ToastMessage.invalidField', { field: 'Email' }));
		}
	};

	const handleSubmit = async (e: any) => {
		e.preventDefault();
		if (otp.length !== 4) {
			setError(translate('ToastMessage.pleaseEnterOtp'));
			return;
		}
		setError(null);
		const payload = {
			email: userEmail,
			otp,
		};
		verifyOTPMutation(payload)
			.then((res: any) => {
				if (res?.success && res?.data) {
					navigate(`/reset-password?token=${res.data}&userType=${userType}`);
				} else {
					setError(res?.message || translate('ToastMessage.failedToVerifyOtp')); // Handle unsuccessful response
				}
			})
			.catch((error) => {
				console.error(translate('ToastMessage.failedToVerifyOtp'), error);
				setError(translate('ToastMessage.errorOccurredWhileVerifyingOtp')); // Notify the user of the failure
			});
	};

	useEffect(() => {
		const keyDownHandler = (event: any) => {
			if (event.key === 'Enter') {
				handleSubmit(event);
			}
		};

		document.addEventListener('keydown', keyDownHandler);

		return () => {
			document.removeEventListener('keydown', keyDownHandler);
		};
	}, [otp]);

	useEffect(() => {
		let countdown: any = null;

		if (isTimerRunning) {
			countdown = setInterval(() => {
				setTimer((prevTimer) => prevTimer - 1);
			}, 1000);
		}

		return () => clearInterval(countdown);
	}, [isTimerRunning]);

	useEffect(() => {
		if (timer === 0) {
			setIsTimerRunning(false);
		}
	}, [timer]);

	return (
		<div className="auth bg-white d-flex flex-column flex-lg-row">
			<div className="auth-left-content">
				<AuthLeftPanel />
			</div>
			<div className="auth-right-content flex-1 d-flex align-items-center justify-content-center position-relative">
				<BackBtn />
				<div className="auth-form mb-0 mt-5 mx-3 my-5 m-md-5">
					<div className="auth-form-heading text-center">
						<h2 className="mb-3">
							{translate('Common.verify')}{' '}
							<span className="fw-bold">{translate('Common.otp')}</span>
						</h2>
						<p className="mb-0">{translate('Auth.authVerifyOtpDescription')}</p>
					</div>
					<div className="auth-form-fields">
						<Form className="form" onSubmit={handleSubmit}>
							<div className="otp-box position-relative">
								<OtpInput
									value={otp}
									onChange={setOtp}
									numInputs={4}
									renderSeparator={<span>-</span>}
									renderInput={(props, idx) => <input {...props} autoFocus={idx === 0} />}
									inputType={isMobile ? 'number' : 'text'}
								/>
								{error && <div className="f-error text-danger">{error}</div>}
								{message && <div className="f-error text-success">{message}</div>}
							</div>
							<div className="form-btn">
								<Button className="filled-btn rounded-pill w-100" type="submit">
									{translate('Common.submit')}
								</Button>
							</div>
							<p className="mb-0 text-center">
								{translate('Common.didNotReceiveOtp')}{' '}
								<Button
									variant="link"
									onClick={() => {
										if (!isTimerRunning) {
											setIsTimerRunning(true);
										}
										handleRequestOtp();
									}}
									disabled={isTimerRunning}
									className="fw-bold join-link p-0 border-0"
								>
									{isTimerRunning
										? `${translate('Common.waitFor')} ${timer} ${translate('Common.seconds')}`
										: `${translate('Common.resend')}`}
								</Button>
							</p>
						</Form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VerifyOTP;
